/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Card,
  CardMedia,
  Link,
  Typography
} from '@one-thd/sui-atomic-components';
import {
  arrayOf as arrayOfType,
  params,
  shape,
  string as stringType,
  useDataModel,
  extend,
} from '@thd-nucleus/data-sources';
import {
  StorePod,
  StorePodHours,
  StorePodPhoneNumber,
  StorePodAddress,
  StorePodMap,
} from '@thd-olt-component-react/store-pod';

const checkIfGrandOpening = (storeGrandOpeningData) => {
  if (!storeGrandOpeningData) {
    return false;
  }

  const currentDate = new Date();
  const expirationDate = new Date(storeGrandOpeningData?.expires);

  return currentDate < expirationDate;
};

export const ProDeskHero = ({ storeNumber, pageName = '' }) => {
  const { data, error, loading } = useDataModel('storeSearch', {
    variables: {
      storeSearchInput: storeNumber,
      pagesize: '1',
    },
  });

  const { data: emtData, loading: emtLoading } = useDataModel('emtContent', {
    variables: {
      contentId: storeNumber,
      pageName
    },
    skip: !storeNumber || !pageName
  });

  const grandOpeningStore = emtData?.emtContent?.content?.grandOpeningBanner?.[storeNumber];

  const isGrandOpening = checkIfGrandOpening(grandOpeningStore);

  const storeInfo = data?.storeSearch?.stores[0];

  if (!data || loading || error || !storeInfo) return null;

  const storeAddress = `${storeInfo.address?.street}, 
  ${storeInfo.address?.city}, ${storeInfo.address?.state} ${storeInfo.address?.postalCode}`;

  const StorePhoneNumbers = () => {
    const numbersToInclude = storeInfo.proDeskPhone ? ['pro', 'store', 'tool'] : ['store', 'tool'];
    return (
      <>
        {numbersToInclude.map((phoneNumber) => (
          <div className="sui-ml-2 sui-mt-2">
            <StorePodPhoneNumber boldLabel displayMultiline type={phoneNumber} />
          </div>
        ))}
      </>
    );
  };

  const handleChangeStoreClick = () => {
    document.dispatchEvent(new CustomEvent('launchLocDrawer'));
  };

  return (
    <>
      <div className="sui-flex sui-flex-col sm:sui-flex-row md:sui-gap-4 md:sui-items-end">
        <Typography variant="h1">
          <Link variant="h1" underline="hover" href={storeInfo.storeDetailsPageLink}>
            Pro Desk at {storeInfo.name} #{storeNumber}
          </Link>
        </Typography>
        <Link align="left" component="button" onClick={() => handleChangeStoreClick()}>
          Change Store
        </Link>
      </div>
      <div className="sui-mt-2 sui-grid md:sui-grid-cols-6 sui-grid-cols-2 ">
        <Button
          fullWidth
          href={`https://www.homedepot.com/c/localad?flyer_type_name=proad?StoreRef=${storeNumber}`}
        >
          Pro Ad
        </Button>
      </div>
      <div className="sui-mt-2 sui-grid sui-gap-3 sui-grid-cols-12">
        <div className="sui-col-span-12 lg:sui-col-span-8 sui-flex sui-w-full sui-h-full">
          <Card PaperProps={{ className: 'sui-size-full sui-flex sui-items-stretch' }}>
            <div className="sui-flex sui-flex-col sui-gap-8">
              <Typography variant="h2">
                Do you have everything you need for your project?
              </Typography>
              <CardMedia
                // eslint-disable-next-line max-len
                src="https://dam.thdstatic.com/content/production/UzFpCaOH5bTq4fxt_ur3sQ/LtpQZzzYDZ5N3i-gV-W9tQ/Original%20file/WF-6162000-Pro-Store-Page-Hero-Banner.jpg?Resize=(899.33,180)"
                alt="a pro checking out at the home depot store"
              />
            </div>
            <div className="sui-flex sui-flex-1 sui-flex-col sm:sui-flex-row sm:sui-items-center sui-my-4 sui-gap-8">
              <img
                src="https://assets.thdstatic.com/images/v1/logo-how-doers-get-more-done.png"
                alt="The Home Depot Logo"
                height="50"
                width="185"
              />
              <Typography variant="body-base">
                Explore Your Local Pro Desk at a Home Depot Near You. Unlock additional savings, perks and benefits
                with <Link href="https://www.homedepot.com/c/Pro_Xtra">Pro Xtra</Link>: Home Depot’s free
                loyalty program
              </Typography>
            </div>
          </Card>
        </div>
        <div className="sui-col-span-12 lg:sui-col-span-4 sui-flex sui-w-full sui-h-full">
          <Card PaperProps={{ className: 'sui-size-full' }}>
            <Typography variant="h2"> Pro Desk </Typography>
            <StorePod storeId={storeNumber}>
              <div className="sui-grid sui-gap-8 sui-grid-cols-2">
                <div>
                  <Typography variant="body-lg" weight="bold">
                    Contact Us
                  </Typography>
                  <StorePhoneNumbers />
                </div>
                {!isGrandOpening && !emtLoading && (
                  <div>
                    <Typography variant="body-lg" weight="bold">
                      Store Hours
                    </Typography>
                    <div className="sui-grid-cols-2 sui-ml-2 sui-mt-2">
                      <StorePodHours displayMultiline hoursType="proDesk" />
                      <div className="sui-mt-4">
                        <StorePodHours displayMultiline hoursType="curbside" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="sui-mt-3">
                <Typography variant="body-lg" weight="bold">
                  Location
                </Typography>
              </div>
              <div className="sui-grid sui-gap-8 sui-grid-cols-2">
                <div className="sui-ml-2">
                  <StorePodAddress />
                </div>
                <div>
                  <StorePodMap
                    className="sui-items-end"
                    mapOptions={{
                      height: '60',
                      width: '200',
                    }}
                  />
                </div>
              </div>
              <div className="sui-mt-3 sui-grid sui-gap-8 sui-grid-cols-2">
                <div>
                  <Link href={`https://www.homedepot.com/c/localad?flyer_type_name=proad?StoreRef=${storeNumber}`}>
                    Pro Ad
                  </Link>
                </div>
                <div>
                  <Link href={`https://www.bing.com/mapspreview?where1=${storeAddress}&amp;sty=c`}>
                    Directions
                  </Link>
                </div>
              </div>

              <div className="sui-mt-5">
                <Button href={storeInfo.storeDetailsPageLink} fullWidth>
                  View Store Details
                </Button>
              </div>
            </StorePod>
          </Card>
        </div>
      </div>
    </>
  );
};

ProDeskHero.propTypes = {
  storeNumber: PropTypes.string.isRequired,
  // eslint-disable-next-line react/require-default-props
  pageName: PropTypes.string
};

ProDeskHero.dataModel = extend(
  {
    storeSearch: params({
      storeSearchInput: stringType(),
      pagesize: stringType(),
    }).shape({
      stores: arrayOfType(
        shape({
          storeId: stringType(),
          name: stringType(),
          address: shape({
            street: stringType(),
            city: stringType(),
            state: stringType(),
            postalCode: stringType(),
            country: stringType(),
          }),
          storeDetailsPageLink: stringType(),
          proDeskPhone: stringType(),
        })
      ),
    }),
  },
  StorePod
);
